/**
 * Community-related type definitions for trAInedup.ai
 * Defines interfaces for forums, discussions, messaging, and community features
 */

/**
 * Forum category types
 */
export enum ForumCategory {
  GENERAL = 'general',
  AI_FUNDAMENTALS = 'ai_fundamentals',
  MACHINE_LEARNING = 'machine_learning',
  DEEP_LEARNING = 'deep_learning',
  NLP = 'nlp',
  COMPUTER_VISION = 'computer_vision',
  AI_ETHICS = 'ai_ethics',
  CAREER_ADVICE = 'career_advice',
  PROJECT_SHOWCASE = 'project_showcase',
  HELP_SUPPORT = 'help_support',
  ANNOUNCEMENTS = 'announcements',
}

/**
 * Post types
 */
export enum PostType {
  DISCUSSION = 'discussion',
  QUESTION = 'question',
  ANNOUNCEMENT = 'announcement',
  SHOWCASE = 'showcase',
  POLL = 'poll',
}

/**
 * Message types
 */
export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  LINK = 'link',
  SYSTEM = 'system',
}

/**
 * Notification types
 */
export enum NotificationType {
  MESSAGE = 'message',
  MENTION = 'mention',
  REPLY = 'reply',
  LIKE = 'like',
  FOLLOW = 'follow',
  COURSE_UPDATE = 'course_update',
  SYSTEM = 'system',
}

/**
 * Forum interface
 */
export interface Forum {
  id: string;
  name: string;
  description: string;
  category: ForumCategory;
  slug: string;
  
  // Moderation
  moderators: string[]; // user IDs
  isModerated: boolean;
  
  // Statistics
  postCount: number;
  memberCount: number;
  lastActivity: Date;
  
  // Settings
  isPrivate: boolean;
  requiredSubscriptionTier?: string;
  allowedRoles: string[];
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Discussion post interface
 */
export interface Post {
  id: string;
  forumId: string;
  authorId: string;
  title: string;
  content: string;
  type: PostType;
  
  // Content
  attachments: PostAttachment[];
  tags: string[];
  
  // Engagement
  likes: string[]; // user IDs who liked
  views: number;
  replyCount: number;
  
  // Status
  isPinned: boolean;
  isLocked: boolean;
  isDeleted: boolean;
  
  // Moderation
  isApproved: boolean;
  moderatedBy?: string;
  moderatedAt?: Date;
  moderationReason?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  lastReplyAt?: Date;
}

/**
 * Post attachment interface
 */
export interface PostAttachment {
  id: string;
  type: 'image' | 'file' | 'link';
  url: string;
  filename?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnail?: string;
  title?: string;
  description?: string;
}

/**
 * Reply interface
 */
export interface Reply {
  id: string;
  postId: string;
  authorId: string;
  content: string;
  
  // Threading
  parentReplyId?: string; // for nested replies
  depth: number;
  
  // Content
  attachments: PostAttachment[];
  mentions: string[]; // user IDs mentioned
  
  // Engagement
  likes: string[]; // user IDs who liked
  
  // Status
  isDeleted: boolean;
  isEdited: boolean;
  editedAt?: Date;
  
  // Moderation
  isApproved: boolean;
  moderatedBy?: string;
  moderatedAt?: Date;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Direct message conversation interface
 */
export interface Conversation {
  id: string;
  participants: string[]; // user IDs
  type: 'direct' | 'group';
  
  // Group conversation details
  name?: string;
  description?: string;
  avatar?: string;
  adminIds?: string[];
  
  // Status
  isActive: boolean;
  isArchived: boolean;
  
  // Last message info
  lastMessageId?: string;
  lastMessageAt?: Date;
  lastMessagePreview?: string;
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Message interface
 */
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  type: MessageType;
  content: string;
  
  // Content
  attachments: MessageAttachment[];
  mentions: string[]; // user IDs mentioned
  
  // Threading
  replyToMessageId?: string;
  
  // Status
  isEdited: boolean;
  editedAt?: Date;
  isDeleted: boolean;
  deletedAt?: Date;
  
  // Read status
  readBy: MessageReadStatus[];
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Message attachment interface
 */
export interface MessageAttachment {
  id: string;
  type: 'image' | 'file' | 'audio' | 'video';
  url: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  thumbnail?: string;
  duration?: number; // for audio/video
  dimensions?: { width: number; height: number }; // for images/videos
}

/**
 * Message read status interface
 */
export interface MessageReadStatus {
  userId: string;
  readAt: Date;
}

/**
 * User profile interface for community features
 */
export interface CommunityProfile {
  userId: string;
  displayName: string;
  avatar?: string;
  bio?: string;
  location?: string;
  website?: string;
  
  // Social links
  socialLinks: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    portfolio?: string;
  };
  
  // Community stats
  postCount: number;
  replyCount: number;
  likesReceived: number;
  reputation: number;
  
  // Badges and achievements
  badges: UserBadge[];
  achievements: UserAchievement[];
  
  // Activity
  joinedAt: Date;
  lastActiveAt: Date;
  
  // Settings
  isOnline: boolean;
  showOnlineStatus: boolean;
  allowDirectMessages: boolean;
  emailNotifications: boolean;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * User badge interface
 */
export interface UserBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  earnedAt: Date;
  category: 'achievement' | 'role' | 'special';
}

/**
 * User achievement interface
 */
export interface UserAchievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  points: number;
  earnedAt: Date;
  category: string;
}

/**
 * Notification interface
 */
export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  
  // Related entities
  relatedEntityId?: string; // post ID, message ID, etc.
  relatedEntityType?: string; // 'post', 'message', 'course', etc.
  
  // Sender info (for user-generated notifications)
  senderId?: string;
  senderName?: string;
  senderAvatar?: string;
  
  // Status
  isRead: boolean;
  readAt?: Date;
  
  // Action
  actionUrl?: string;
  actionText?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Follow relationship interface
 */
export interface Follow {
  id: string;
  followerId: string;
  followingId: string;
  createdAt: Date;
}

/**
 * User activity interface
 */
export interface UserActivity {
  id: string;
  userId: string;
  type: 'post_created' | 'reply_created' | 'course_completed' | 'badge_earned' | 'achievement_unlocked';
  description: string;
  
  // Related entities
  relatedEntityId?: string;
  relatedEntityType?: string;
  
  // Visibility
  isPublic: boolean;
  
  // Metadata
  createdAt: Date;
}

/**
 * Moderation report interface
 */
export interface ModerationReport {
  id: string;
  reporterId: string;
  reportedUserId?: string;
  reportedPostId?: string;
  reportedReplyId?: string;
  reportedMessageId?: string;
  
  // Report details
  reason: 'spam' | 'harassment' | 'inappropriate_content' | 'misinformation' | 'other';
  description: string;
  category: string;
  
  // Status
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  
  // Resolution
  resolvedBy?: string;
  resolvedAt?: Date;
  resolution?: string;
  actionTaken?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Community statistics interface
 */
export interface CommunityStats {
  totalUsers: number;
  activeUsers: number; // last 30 days
  totalPosts: number;
  totalReplies: number;
  totalMessages: number;
  
  // Growth metrics
  newUsersThisMonth: number;
  newPostsThisMonth: number;
  engagementRate: number;
  
  // Popular content
  topPosts: string[]; // post IDs
  topContributors: string[]; // user IDs
  trendingTopics: string[];
  
  // Time period
  periodStart: Date;
  periodEnd: Date;
  lastUpdated: Date;
}

/**
 * Forum subscription interface
 */
export interface ForumSubscription {
  id: string;
  userId: string;
  forumId: string;
  notificationLevel: 'all' | 'mentions_only' | 'none';
  createdAt: Date;
}

/**
 * Mention interface
 */
export interface Mention {
  id: string;
  mentionerId: string;
  mentionedUserId: string;
  postId?: string;
  replyId?: string;
  messageId?: string;
  context: string; // surrounding text
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
}

/**
 * Poll interface (for poll-type posts)
 */
export interface Poll {
  id: string;
  postId: string;
  question: string;
  options: PollOption[];
  
  // Settings
  allowMultipleChoices: boolean;
  allowAddOptions: boolean;
  isAnonymous: boolean;
  expiresAt?: Date;
  
  // Results
  totalVotes: number;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Poll option interface
 */
export interface PollOption {
  id: string;
  text: string;
  votes: string[]; // user IDs who voted for this option
  voteCount: number;
}

/**
 * Community event interface
 */
export interface CommunityEvent {
  id: string;
  title: string;
  description: string;
  type: 'webinar' | 'workshop' | 'meetup' | 'conference' | 'study_group';
  
  // Schedule
  startDate: Date;
  endDate: Date;
  timezone: string;
  
  // Location
  isOnline: boolean;
  location?: string;
  meetingUrl?: string;
  
  // Organizer
  organizerId: string;
  coOrganizers: string[];
  
  // Attendance
  attendees: string[]; // user IDs
  maxAttendees?: number;
  waitlist: string[];
  
  // Content
  agenda?: string;
  resources: EventResource[];
  
  // Settings
  isPublic: boolean;
  requiresApproval: boolean;
  requiredSubscriptionTier?: string;
  
  // Status
  status: 'draft' | 'published' | 'ongoing' | 'completed' | 'cancelled';
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Event resource interface
 */
export interface EventResource {
  id: string;
  title: string;
  description?: string;
  type: 'link' | 'file' | 'video';
  url: string;
  isRequired: boolean;
}

const communityTypes = {
  ForumCategory,
  PostType,
  MessageType,
  NotificationType,
};

export default communityTypes;

